import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Header from './components/Header';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import CookieConsent from './components/CookieConsent';
import AgentChat from './components/AgentChat';
import { initGTMIfConsented } from './utils/gtm';

// Home is eagerly loaded — it's the most common entry point and
// should render without any fallback flash on initial visit.
import Home from './pages/Home';

// All other routes are lazy-loaded so they're excluded from the
// initial JS bundle and fetched only when the user navigates to them.
const AIServices = lazy(() => import('./pages/AIServices'));
const MarketingServices = lazy(() => import('./pages/MarketingServices'));

// Landing Pages
const ServicesLanding = lazy(() => import('./pages/ServicesLanding'));
const MarketsLanding = lazy(() => import('./pages/MarketsLanding'));

// Service Pages
const SEOServices = lazy(() => import('./pages/SEOServices'));
const WebsiteServices = lazy(() => import('./pages/WebsiteServices'));
const AIAutomationServices = lazy(() => import('./pages/AIAutomationServices'));
const HubSpotServices = lazy(() => import('./pages/HubSpotServices'));
const CustomAppsServices = lazy(() => import('./pages/CustomAppsServices'));
const PPCServices = lazy(() => import('./pages/PPCServices'));
const ContentMarketingServices = lazy(() => import('./pages/ContentMarketingServices'));
const SocialMediaServices = lazy(() => import('./pages/SocialMediaServices'));

// Market Pages
const RecruitmentMarket = lazy(() => import('./pages/RecruitmentMarket'));
const SaaSMarket = lazy(() => import('./pages/SaaSMarket'));
const HospitalityMarket = lazy(() => import('./pages/HospitalityMarket'));
const EducationMarket = lazy(() => import('./pages/EducationMarket'));
const EnergyMarket = lazy(() => import('./pages/EnergyMarket'));
const EngineeringMarket = lazy(() => import('./pages/EngineeringMarket'));

// Case Studies
const CaseStudies = lazy(() => import('./pages/CaseStudies'));

// Contact
const ContactPage = lazy(() => import('./pages/ContactPage'));

// Legal
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));

// 404
const NotFound = lazy(() => import('./pages/NotFound'));

// Minimal fallback — preserves page height to prevent layout shift
// while the route chunk downloads. No spinner needed; chunks are small.
const PageFallback = () => (
  <div className="min-h-screen bg-white dark:bg-neutral-dark-100" aria-hidden="true" />
);

interface AppContentProps {
  darkMode: boolean;
  setDarkMode: (darkMode: boolean) => void;
}

/**
 * Inner component inside <Router> so it can access useLocation for
 * re-running the scroll-reveal observer on every route change.
 */
function AppContent({ darkMode, setDarkMode }: AppContentProps) {
  const location = useLocation();

  useEffect(() => {
    // Set up IntersectionObserver to trigger .fade-in-up animations
    // as elements scroll into the viewport rather than on mount.
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let observer: IntersectionObserver | null = null;

    // Small delay so the new page's DOM is ready after route transition
    const timer = setTimeout(() => {
      const elements = document.querySelectorAll<HTMLElement>('.fade-in-up:not(.is-visible)');

      if (prefersReduced) {
        elements.forEach(el => el.classList.add('is-visible'));
        return;
      }

      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              observer?.unobserve(entry.target);
            }
          });
        },
        // Trigger when element is 10% visible; -40px bottom margin means
        // elements near the very bottom edge animate slightly before reaching
        // the viewport edge, keeping the page feeling alive.
        { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
      );

      elements.forEach(el => observer!.observe(el));
    }, 50);

    return () => {
      clearTimeout(timer);
      observer?.disconnect();
    };
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-white dark:bg-neutral-dark-200 transition-colors duration-300">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary-indigo focus:text-white focus:rounded-lg focus:font-medium focus:shadow-lg"
      >
        Skip to main content
      </a>
      <Header darkMode={darkMode} setDarkMode={setDarkMode} />
      <main id="main-content">
        <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/ai-services" element={<AIServices />} />
            <Route path="/marketing-services" element={<MarketingServices />} />

            {/* Landing Pages */}
            <Route path="/services" element={<ServicesLanding />} />
            <Route path="/markets" element={<MarketsLanding />} />

            {/* Service Pages */}
            <Route path="/services/seo" element={<SEOServices />} />
            <Route path="/services/website-development" element={<WebsiteServices />} />
            <Route path="/services/ai-automation" element={<AIAutomationServices />} />
            <Route path="/services/hubspot" element={<HubSpotServices />} />
            <Route path="/services/custom-apps" element={<CustomAppsServices />} />
            <Route path="/services/ppc" element={<PPCServices />} />
            <Route path="/services/content-marketing" element={<ContentMarketingServices />} />
            <Route path="/services/social-media" element={<SocialMediaServices />} />

            {/* Market Pages */}
            <Route path="/markets/recruitment" element={<RecruitmentMarket />} />
            <Route path="/markets/saas" element={<SaaSMarket />} />
            <Route path="/markets/hospitality" element={<HospitalityMarket />} />
            <Route path="/markets/education" element={<EducationMarket />} />
            <Route path="/markets/energy" element={<EnergyMarket />} />
            <Route path="/markets/engineering" element={<EngineeringMarket />} />

            {/* Case Studies */}
            <Route path="/case-studies" element={<CaseStudies />} />

            {/* Contact */}
            <Route path="/contact" element={<ContactPage />} />

            {/* Legal */}
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />

            {/* 404 */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
      <CookieConsent />
      <AgentChat />
    </div>
  );
}

function App() {
  const [darkMode, setDarkMode] = useState(false);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  useEffect(() => {
    initGTMIfConsented();
  }, []);

  return (
    <HelmetProvider>
      <Router>
        <ScrollToTop />
        <AppContent darkMode={darkMode} setDarkMode={setDarkMode} />
      </Router>
    </HelmetProvider>
  );
}

export default App;
